import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BriefcaseBusiness, Scale, Wallet, BarChart3 } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const UI_COLORS = {
  indigo: '#4f46e5',
  sky: '#0ea5e9',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444'
};

const translateRuolo = (value) => {
  const map = { commerciale: 'Commerciale', legale: 'Legale', pagamenti: 'Pagamenti' };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const formatTopInadempienzeTitle = (ruolo) => {
  if (!ruolo) return 'Distribuzione inadempienze (tutti i ruoli)';
  return `Distribuzione inadempienze per ${translateRuolo(ruolo).toLowerCase()}`;
};

const truncate = (value, max = 44) => {
  const s = String(value || '').trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
};

const translateGravita = (value) => {
  const map = { standard: 'Standard', grave: 'Grave', bloccante: 'Bloccante' };
  return map[String(value || '').toLowerCase()] || (value ?? '');
};

const GravitaChip = ({ gravita }) => {
  const key = String(gravita || '').toLowerCase();
  const color =
    key === 'bloccante' ? UI_COLORS.red : key === 'grave' ? UI_COLORS.amber : key === 'standard' ? UI_COLORS.indigo : '';
  return (
    <Chip
      label={translateGravita(key) || '-'}
      size="small"
      variant="outlined"
      sx={{
        textTransform: 'capitalize',
        fontWeight: 700,
        color: color || 'text.secondary',
        borderColor: color || 'rgba(145, 158, 171, 0.34)'
      }}
    />
  );
};

const gravitaColor = (gravita) => {
  const key = String(gravita || '').toLowerCase();
  if (key === 'bloccante') return UI_COLORS.red;
  if (key === 'grave') return UI_COLORS.amber;
  if (key === 'standard') return UI_COLORS.indigo;
  return UI_COLORS.amber;
};

const PercentDonut = ({ value }) => {
  const raw = Number(value || 0);
  const pct = Number.isFinite(raw) ? Math.max(0, Math.min(100, Math.round(raw))) : 0;
  const size = 40;
  const thickness = 5;
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', width: size, height: size }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{ color: 'rgba(15, 23, 42, 0.10)' }}
      />
      <CircularProgress
        variant="determinate"
        value={pct}
        size={size}
        thickness={thickness}
        sx={{ position: 'absolute', left: 0, color: UI_COLORS.amber }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1 }}>
          {pct}%
        </Typography>
      </Box>
    </Box>
  );
};

const formatRoleSubtitle = (ruolo) => {
  if (!ruolo) return 'tutti i ruoli';
  return translateRuolo(ruolo).toLowerCase();
};

const formatDateIso = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDateIt = (value) => {
  const s = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
};

const addDays = (d, delta) => {
  const x = new Date(d);
  x.setDate(x.getDate() + delta);
  return x;
};

const getPresetRange = (preset) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (preset === 'last_7_days') {
    return { from: formatDateIso(addDays(today, -6)), to: formatDateIso(today) };
  }
  if (preset === 'last_30_days') {
    return { from: formatDateIso(addDays(today, -29)), to: formatDateIso(today) };
  }
  if (preset === 'last_365_days') {
    return { from: formatDateIso(addDays(today, -364)), to: formatDateIso(today) };
  }
  if (preset === 'month_current') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: formatDateIso(from), to: formatDateIso(to) };
  }
  if (preset === 'month_previous') {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: formatDateIso(from), to: formatDateIso(to) };
  }
  return { from: '', to: '' };
};

const presetLabel = (preset) => {
  const map = {
    last_7_days: 'Ultima settimana',
    last_30_days: 'Ultimo mese',
    last_365_days: 'Ultimo anno',
    month_current: 'Mese corrente',
    month_previous: 'Mese precedente',
    custom: 'Periodo'
  };
  return map[preset] || 'Periodo';
};

const formatSurnameName = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '-';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0];
  const surname = parts[parts.length - 1];
  const name = parts.slice(0, -1).join(' ');
  return `${surname} ${name}`.trim();
};

const StatItem = ({ title, value, accent }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={{
      p: 2.2,
      borderRadius: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
      {title}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 900, mt: 1, color: accent || 'text.primary' }}>
      {value}
    </Typography>
  </Paper>
);

const RoleCard = ({ label, value, total, icon: Icon, color }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.2 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            bgcolor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color
          }}
        >
          <Icon size={18} />
        </Box>
        <Typography sx={{ fontWeight: 800 }}>{label}</Typography>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 1.2 }}>
        {value}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 7,
          borderRadius: 99,
          bgcolor: `${color}15`,
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 99 }
        }}
      />
    </Paper>
  );
};

const formatEuro = (value) =>
  Number(value || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatTrendTick = (value) => {
  const s = String(value || '').trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s.slice(0, 5);
  return s;
};

export default function BlacklistKpi() {
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [overviewTopTab, setOverviewTopTab] = useState('users');
  const [periodPreset, setPeriodPreset] = useState('last_30_days');
  const initialRange = useMemo(() => getPresetRange('last_30_days'), []);
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [ruolo, setRuolo] = useState('');
  const [trendView, setTrendView] = useState('range');
  const [trendYear, setTrendYear] = useState(currentYear);
  const [data, setData] = useState(null);

  const yearOptions = useMemo(() => {
    const set = new Set([currentYear, currentYear - 1, currentYear - 2]);
    const fromYear = String(dateFrom || '').slice(0, 4);
    const toYear = String(dateTo || '').slice(0, 4);
    if (/^\d{4}$/.test(fromYear)) set.add(Number.parseInt(fromYear, 10));
    if (/^\d{4}$/.test(toYear)) set.add(Number.parseInt(toYear, 10));
    set.add(Number(trendYear));
    return Array.from(set).filter(Number.isFinite).sort((a, b) => b - a);
  }, [dateFrom, dateTo, trendYear, currentYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/blacklist/kpi', {
        params: {
          from: dateFrom || undefined,
          to: dateTo || undefined,
          ruolo: ruolo || undefined,
          trend_mode: trendView === 'year' ? 'year' : undefined,
          year: trendView === 'year' ? trendYear : undefined
        }
      });
      setData(res.data || null);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [ruolo, dateFrom, dateTo, trendView, trendYear]);

  const totals = data?.totals || {};
  const byRole = data?.by_role || {};
  const byStatus = data?.by_status || {};
  const dannoByRole = Array.isArray(data?.danno_by_role) ? data.danno_by_role : [];
  const dannoBuckets = Array.isArray(data?.danno_buckets) ? data.danno_buckets : [];
  const trend = Array.isArray(data?.trend) ? data.trend : [];
  const trendYearRows = Array.isArray(data?.trend_year) ? data.trend_year : [];
  const topInadempienze = Array.isArray(data?.top_inadempienze) ? data.top_inadempienze : [];
  const top = Array.isArray(data?.top) ? data.top : [];

  const rolesTotal = useMemo(
    () => Number(byRole.commerciale || 0) + Number(byRole.legale || 0) + Number(byRole.pagamenti || 0),
    [byRole]
  );

  const subtitle = useMemo(() => {
    const rangeLabel =
      dateFrom && dateTo ? `${formatDateIt(dateFrom)} → ${formatDateIt(dateTo)}` : '—';
    return `${presetLabel(periodPreset)} • ${rangeLabel}, ${formatRoleSubtitle(ruolo)}`;
  }, [periodPreset, dateFrom, dateTo, ruolo]);

  const statusChartData = useMemo(() => {
    const list = [
      { key: 'aperta', label: 'Aperta' },
      { key: 'in_verifica', label: 'In verifica' },
      { key: 'confermata', label: 'Confermata' },
      { key: 'archiviata', label: 'Archiviata' },
      { key: 'respinta', label: 'Respinta' }
    ];
    return list.map((s) => ({ ...s, value: Number(byStatus[s.key] || 0) }));
  }, [byStatus]);

  const dannoByRoleChartData = useMemo(
    () =>
      (dannoByRole || []).map((r) => ({
        ruolo: translateRuolo(r.ruolo),
        danno_totale: Number(r.danno_totale || 0),
        danno_medio: Number(r.danno_medio || 0),
        segnalazioni_totali: Number(r.segnalazioni_totali || 0)
      })),
    [dannoByRole]
  );

  const trendChartData = useMemo(
    () =>
      (trend || []).map((r) => ({
        label: r.label || r.period,
        segnalazioni_totali: Number(r.segnalazioni_totali || 0),
        segnalazioni_confermate: Number(r.segnalazioni_confermate || 0),
        danno_totale: Number(r.danno_totale || 0)
      })),
    [trend]
  );

  const trendYearChartData = useMemo(
    () =>
      (trendYearRows || []).map((r) => ({
        label: r.label || r.period,
        segnalazioni_totali: Number(r.segnalazioni_totali || 0),
        segnalazioni_confermate: Number(r.segnalazioni_confermate || 0),
        danno_totale: Number(r.danno_totale || 0)
      })),
    [trendYearRows]
  );

  const activeTrendData = trendView === 'year' ? trendYearChartData : trendChartData;

  return (
    <Box sx={{ p: 3 }}>
      <StyledCard>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              alignItems: { xs: 'stretch', md: 'flex-start' },
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  bgcolor: `${UI_COLORS.indigo}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <BarChart3 size={20} color={UI_COLORS.indigo} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  Dashboard KPI blacklist
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                width: { xs: '100%', md: 'auto' }
              }}
            >
              <FormControl size="small" sx={{ minWidth: 160, flex: { xs: '1 1 160px', lg: '0 0 auto' } }}>
                <InputLabel>Intervallo</InputLabel>
                <Select
                  label="Intervallo"
                  value={periodPreset}
                  onChange={(e) => {
                    const next = String(e.target.value || '');
                    setPeriodPreset(next);
                    const range = getPresetRange(next);
                    if (range.from && range.to) {
                      setDateFrom(range.from);
                      setDateTo(range.to);
                    }
                  }}
                >
                  <MenuItem value="last_7_days">Ultima settimana</MenuItem>
                  <MenuItem value="last_30_days">Ultimo mese</MenuItem>
                  <MenuItem value="last_365_days">Ultimo anno</MenuItem>
                  <MenuItem value="month_current">Mese corrente</MenuItem>
                  <MenuItem value="month_previous">Mese precedente</MenuItem>
                  <MenuItem value="custom">Personalizzato</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                type="date"
                label="Da"
                value={dateFrom}
                onChange={(e) => {
                  setPeriodPreset('custom');
                  setDateFrom(String(e.target.value || ''));
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140, flex: { xs: '1 1 140px', lg: '0 0 auto' } }}
              />
              <TextField
                size="small"
                type="date"
                label="A"
                value={dateTo}
                onChange={(e) => {
                  setPeriodPreset('custom');
                  setDateTo(String(e.target.value || ''));
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 140, flex: { xs: '1 1 140px', lg: '0 0 auto' } }}
              />

              <FormControl size="small" sx={{ minWidth: 170, flex: { xs: '1 1 170px', lg: '0 0 auto' } }}>
                <InputLabel>Tutti i ruoli</InputLabel>
                <Select label="Tutti i ruoli" value={ruolo} onChange={(e) => setRuolo(String(e.target.value || ''))}>
                  <MenuItem value="">Tutti i ruoli</MenuItem>
                  <MenuItem value="commerciale">Commerciale</MenuItem>
                  <MenuItem value="legale">Legale</MenuItem>
                  <MenuItem value="pagamenti">Pagamenti</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  mb: 2.5,
                  minHeight: 44,
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 44 }
                }}
              >
                <Tab value="overview" label="Panoramica" />
                <Tab value="economic" label="Impatto economico" />
                <Tab value="inadempienze" label="Inadempienze" />
                <Tab value="trend" label="Trend" />
              </Tabs>

              {tab === 'overview' ? (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                      <StatItem title="Segnalazioni totali" value={Number(totals.segnalazioni_totali || 0)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatItem title="Confermate" value={Number(totals.segnalazioni_confermate || 0)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatItem title="Casi bloccanti" value={Number(totals.casi_bloccanti || 0)} accent={UI_COLORS.red} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatItem title="Recidivi (2+ richiami)" value={Number(totals.recidivi_2plus || 0)} />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={5}>
                      <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2, height: '100%' }}>
                        <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Segnalazioni per stato</Typography>
                        <Box sx={{ height: 240 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={statusChartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Bar dataKey="value" name="Segnalazioni" fill={UI_COLORS.indigo} radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Distribuzione per ruolo</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <RoleCard
                            label="Commerciale"
                            value={Number(byRole.commerciale || 0)}
                            total={rolesTotal}
                            icon={BriefcaseBusiness}
                            color={UI_COLORS.indigo}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <RoleCard
                            label="Legale"
                            value={Number(byRole.legale || 0)}
                            total={rolesTotal}
                            icon={Scale}
                            color={UI_COLORS.sky}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <RoleCard
                            label="Pagamenti"
                            value={Number(byRole.pagamenti || 0)}
                            total={rolesTotal}
                            icon={Wallet}
                            color={UI_COLORS.green}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Paper elevation={0} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        px: 2,
                        pt: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        flexWrap: { xs: 'wrap', md: 'nowrap' }
                      }}
                    >
                      <Tabs
                        value={overviewTopTab}
                        onChange={(_, v) => setOverviewTopTab(v)}
                        sx={{
                          minHeight: 40,
                          '& .MuiTab-root': { textTransform: 'none', fontWeight: 900, minHeight: 40 }
                        }}
                      >
                        <Tab value="users" label="Top segnalati" />
                        <Tab value="types" label="Top inadempienze" />
                      </Tabs>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {overviewTopTab === 'users' ? (
                              <>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary' }}>Utente</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary' }}>Ruolo</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary' }}>Segnalazioni</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary' }}>Richiami</TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary' }}>Inadempienza</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: 'text.secondary' }}>Gravità</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                                  Segnalazioni
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                                  % sul totale
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overviewTopTab === 'users' && top.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ py: 3 }} align="center">
                                Nessun dato
                              </TableCell>
                            </TableRow>
                          ) : null}

                          {overviewTopTab === 'users'
                            ? top.map((row) => (
                                <TableRow key={`${row.utente_segnalato_id}-${row.ruolo_segnalato}`}>
                                  <TableCell sx={{ fontWeight: 800 }}>{formatSurnameName(row.utente_name)}</TableCell>
                                  <TableCell>{translateRuolo(row.ruolo_segnalato)}</TableCell>
                                  <TableCell sx={{ fontWeight: 800 }}>{Number(row.segnalazioni_count || 0)}</TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 800,
                                      color: Number(row.richiami_count || 0) > 0 ? UI_COLORS.red : 'text.primary'
                                    }}
                                  >
                                    {Number(row.richiami_count || 0)}
                                  </TableCell>
                                </TableRow>
                              ))
                            : null}

                          {overviewTopTab === 'types' && topInadempienze.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} sx={{ py: 3 }} align="center">
                                Nessun dato
                              </TableCell>
                            </TableRow>
                          ) : null}

                          {overviewTopTab === 'types'
                            ? topInadempienze.map((row) => (
                                <TableRow key={row.tipo_inadempienza_id || row.descrizione}>
                                  <TableCell sx={{ fontWeight: 800 }}>{row.descrizione || '-'}</TableCell>
                                  <TableCell>
                                    <GravitaChip gravita={row.gravita} />
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                                    {Number(row.segnalazioni_count || 0)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <PercentDonut
                                      value={
                                        Number(totals.segnalazioni_totali || 0) > 0
                                          ? (Number(row.segnalazioni_count || 0) / Number(totals.segnalazioni_totali || 0)) *
                                            100
                                          : 0
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              ))
                            : null}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Box>
              ) : null}

              {tab === 'economic' ? (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                      <StatItem title="Danno stimato totale" value={`€ ${formatEuro(totals.danno_totale)}`} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatItem title="Danno stimato medio" value={`€ ${formatEuro(totals.danno_medio)}`} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatItem title="Danno stimato massimo" value={`€ ${formatEuro(totals.danno_massimo)}`} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <StatItem title="Segnalazioni con danno" value={`${Number(totals.pct_segnalazioni_con_danno || 0)}%`} />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2, height: '100%' }}>
                        <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Distribuzione danno (fasce)</Typography>
                        <Box sx={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dannoBuckets} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={0} />
                              <YAxis allowDecimals={false} />
                              <Tooltip />
                              <Bar dataKey="count" name="Segnalazioni" fill={UI_COLORS.amber} radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2, height: '100%' }}>
                        <Typography sx={{ fontWeight: 900, mb: 1.5 }}>Danno stimato per ruolo</Typography>
                        <Box sx={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dannoByRoleChartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="ruolo" tick={{ fontSize: 12 }} interval={0} />
                              <YAxis tickFormatter={(v) => `€${Math.round(Number(v || 0) / 1000)}k`} />
                              <Tooltip formatter={(v) => `€ ${formatEuro(v)}`} />
                              <Legend />
                              <Bar dataKey="danno_totale" name="Totale €" fill={UI_COLORS.indigo} radius={[6, 6, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              ) : null}

              {tab === 'inadempienze' ? (
                <Box>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2 }}>
                    <Typography sx={{ fontWeight: 900, mb: 1.5 }}>{formatTopInadempienzeTitle(ruolo)}</Typography>
                    <Box sx={{ height: Math.max(320, (topInadempienze?.length || 0) * 34) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={topInadempienze}
                          layout="vertical"
                          margin={{ top: 8, right: 18, left: 0, bottom: 8 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis
                            type="category"
                            dataKey="descrizione"
                            width={360}
                            tick={{ fontSize: 12 }}
                            tickFormatter={(v) => truncate(v, 46)}
                          />
                          <Tooltip
                            formatter={(v) => String(v)}
                            labelFormatter={(label) => String(label || '')}
                          />
                          <Bar dataKey="segnalazioni_count" name="Segnalazioni" radius={[6, 6, 6, 6]}>
                            {(topInadempienze || []).map((entry) => (
                              <Cell
                                key={`cell-${entry?.tipo_inadempienza_id || entry?.descrizione || ''}`}
                                fill={gravitaColor(entry?.gravita)}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Box>
              ) : null}

              {tab === 'trend' ? (
                <Box>
                  <Paper elevation={0} variant="outlined" sx={{ p: 2.2, borderRadius: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        alignItems: { xs: 'stretch', md: 'center' },
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        mb: 1.5
                      }}
                    >
                      <Typography sx={{ fontWeight: 900 }}>
                        {trendView === 'year'
                          ? `Trend mensile (Gen–Dic) • ${trendYear}`
                          : 'Trend segnalazioni e danno stimato'}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          flexWrap: { xs: 'wrap', md: 'nowrap' },
                          width: { xs: '100%', md: 'auto' },
                          justifyContent: { xs: 'flex-start', md: 'flex-end' }
                        }}
                      >
                        <FormControl size="small" sx={{ minWidth: 190, flex: { xs: '1 1 190px', md: '0 0 auto' } }}>
                          <InputLabel>Vista</InputLabel>
                          <Select
                            label="Vista"
                            value={trendView}
                            onChange={(e) => {
                              const next = String(e.target.value || 'range');
                              setTrendView(next);
                              if (next === 'year') {
                                const y = Number.parseInt(String(dateFrom || '').slice(0, 4), 10);
                                if (Number.isFinite(y)) setTrendYear(y);
                              }
                            }}
                          >
                            <MenuItem value="range">Periodo selezionato</MenuItem>
                            <MenuItem value="year">Anno (gen–dic)</MenuItem>
                          </Select>
                        </FormControl>

                        {trendView === 'year' ? (
                          <FormControl size="small" sx={{ minWidth: 120, flex: { xs: '1 1 120px', md: '0 0 auto' } }}>
                            <InputLabel>Anno</InputLabel>
                            <Select
                              label="Anno"
                              value={trendYear}
                              onChange={(e) => setTrendYear(Number.parseInt(String(e.target.value || currentYear), 10))}
                            >
                              {yearOptions.map((y) => (
                                <MenuItem key={y} value={y}>
                                  {y}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : null}
                      </Box>
                    </Box>
                    <Box sx={{ height: 320 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={activeTrendData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 12 }}
                            tickFormatter={trendView === 'year' ? undefined : formatTrendTick}
                            interval={trendView === 'year' ? 0 : 'preserveStartEnd'}
                            minTickGap={trendView === 'year' ? undefined : 18}
                            angle={trendView === 'year' ? 0 : -35}
                            textAnchor={trendView === 'year' ? 'middle' : 'end'}
                            height={trendView === 'year' ? 34 : 52}
                          />
                          <YAxis yAxisId="left" allowDecimals={false} />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tickFormatter={(v) => `€${Math.round(Number(v || 0) / 1000)}k`}
                          />
                          <Tooltip
                            formatter={(value, name) => {
                              if (name === 'Danno stimato') return `€ ${formatEuro(value)}`;
                              return String(value);
                            }}
                          />
                          <Legend />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="segnalazioni_totali"
                            name="Segnalazioni"
                            stroke={UI_COLORS.indigo}
                            strokeWidth={2.5}
                            dot={false}
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="segnalazioni_confermate"
                            name="Confermate"
                            stroke={UI_COLORS.green}
                            strokeWidth={2.5}
                            dot={false}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="danno_totale"
                            name="Danno stimato"
                            stroke={UI_COLORS.amber}
                            strokeWidth={2.5}
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Box>
              ) : null}
            </Box>
          )}
        </CardContent>
      </StyledCard>
    </Box>
  );
}
